import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { engineersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  UserPlus,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
  Users,
  Mail,
  Phone,
  Palette,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'engineer', label: 'Engineer' },
  { value: 'installer', label: 'Installer' },
  { value: 'electrician', label: 'Electrician' },
  { value: 'supervisor', label: 'Supervisor' },
];

const COLORS = [
  '#f59e0b', // Amber
  '#059669', // Emerald
  '#3b82f6', // Blue
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#f97316', // Orange
  '#84cc16', // Lime
];

export const Engineers = () => {
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEngineer, setEditingEngineer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'engineer',
    color: '#f59e0b',
  });
  const [saving, setSaving] = useState(false);

  const fetchEngineers = async () => {
    setLoading(true);
    try {
      const response = await engineersAPI.getAll(false); // Get all including inactive
      setEngineers(response.data);
    } catch (error) {
      console.error('Failed to fetch engineers:', error);
      toast.error('Failed to load engineers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEngineers();
  }, []);

  const openNewDialog = () => {
    setEditingEngineer(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'engineer',
      color: COLORS[engineers.length % COLORS.length],
    });
    setShowDialog(true);
  };

  const openEditDialog = (engineer) => {
    setEditingEngineer(engineer);
    setFormData({
      name: engineer.name,
      email: engineer.email,
      phone: engineer.phone,
      role: engineer.role,
      color: engineer.color,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      if (editingEngineer) {
        await engineersAPI.update(editingEngineer.id, formData);
        toast.success('Engineer updated');
      } else {
        await engineersAPI.create(formData);
        toast.success('Engineer added');
      }
      setShowDialog(false);
      fetchEngineers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this engineer?')) return;
    try {
      await engineersAPI.delete(id);
      toast.success('Engineer deactivated');
      fetchEngineers();
    } catch (error) {
      toast.error('Failed to deactivate');
    }
  };

  const handleReactivate = async (id) => {
    try {
      await engineersAPI.update(id, { active: true });
      toast.success('Engineer reactivated');
      fetchEngineers();
    } catch (error) {
      toast.error('Failed to reactivate');
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="engineers-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">Engineers & Team</h1>
            <p className="text-slate-500 mt-1">Manage your installation team members</p>
          </div>
          <Button 
            onClick={openNewDialog}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="add-engineer-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Engineer
          </Button>
        </div>

        {/* Engineers List */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : engineers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Users className="w-16 h-16 mb-4" />
                <p className="text-lg font-medium">No engineers yet</p>
                <p className="text-sm mt-1">Add your first team member to get started</p>
                <Button 
                  className="mt-4 bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={openNewDialog}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Engineer
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Name</TableHead>
                    <TableHead className="font-semibold">Contact</TableHead>
                    <TableHead className="font-semibold">Role</TableHead>
                    <TableHead className="font-semibold">Color</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {engineers.map((engineer) => (
                    <TableRow 
                      key={engineer.id}
                      className={`table-row-hover ${!engineer.active ? 'opacity-50' : ''}`}
                      data-testid={`engineer-row-${engineer.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                            style={{ backgroundColor: engineer.color }}
                          >
                            {engineer.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{engineer.name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-slate-700">
                            <Mail className="w-3 h-3 text-slate-400" />
                            {engineer.email}
                          </div>
                          {engineer.phone && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Phone className="w-3 h-3 text-slate-400" />
                              {engineer.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize text-slate-700">{engineer.role}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-6 h-6 rounded-full border-2 border-white shadow"
                            style={{ backgroundColor: engineer.color }}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        {engineer.active ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500 border-slate-200">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(engineer)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {engineer.active ? (
                              <DropdownMenuItem 
                                onClick={() => handleDelete(engineer.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Deactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleReactivate(engineer.id)}>
                                Reactivate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingEngineer ? 'Edit Engineer' : 'Add Engineer'}
              </DialogTitle>
              <DialogDescription>
                {editingEngineer 
                  ? 'Update the engineer\'s details'
                  : 'Add a new team member to assign installations'
                }
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Smith"
                  data-testid="engineer-name"
                />
              </div>

              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@helios.com"
                  data-testid="engineer-email"
                />
              </div>

              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="0412 345 678"
                  data-testid="engineer-phone"
                />
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData({...formData, role: v})}
                >
                  <SelectTrigger data-testid="engineer-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Calendar Color</Label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${
                        formData.color === color 
                          ? 'border-slate-900 scale-110' 
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData({...formData, color})}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="save-engineer"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingEngineer ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
